import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Loader2, Pencil, Plus, Wifi } from 'lucide-react';
import { useMemo, useState } from 'react';
import { HelpHint } from '../components/HelpHint';
import { NetworkEditor, type EditableElement } from '../components/NetworkEditor';
import { NetworkMap, type MapElement } from '../components/NetworkMap';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

type Element = {
  id: string;
  parentId: string | null;
  type: string;
  name: string;
  identifier: string | null;
  status: string;
  areaId?: string | null;
  area?: { id: string; name: string; zone: { code: string; name: string } } | null;
};

const STATUS_STYLE: Record<string, string> = {
  up: 'bg-secondary-50 text-secondary-700',
  degraded: 'bg-accent-50 text-accent-600',
  down: 'bg-danger-50 text-danger-600',
  decommissioned: 'bg-muted text-muted-foreground',
};

function Node({
  element,
  childrenOf,
  depth,
  onSelect,
  onEdit,
}: {
  element: Element;
  childrenOf: Map<string | null, Element[]>;
  depth: number;
  onSelect: (element: Element) => void;
  /** Undefined for anyone without network:write — the pencil is not rendered. */
  onEdit?: (element: Element) => void;
}) {
  const [open, setOpen] = useState(depth < 2);
  const children = childrenOf.get(element.id) ?? [];

  return (
    <li>
      <div
        className="flex items-center gap-2 rounded-lg py-1.5 hover:bg-muted/40"
        style={{ paddingLeft: `${depth * 18}px` }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`flex h-4 w-4 items-center justify-center ${children.length ? '' : 'invisible'}`}
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          <ChevronRight
            className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
        </button>

        <button type="button" onClick={() => onSelect(element)} className="flex-1 text-left text-sm">
          <span className="font-medium">{element.name}</span>
          <span className="label-mono ml-2 text-xs text-muted-foreground">{element.type}</span>
          {element.identifier && (
            <span className="ml-2 text-xs text-muted-foreground">{element.identifier}</span>
          )}
        </button>

        <span className={`badge ${STATUS_STYLE[element.status] ?? ''}`}>{element.status}</span>

        {onEdit && (
          <button
            type="button"
            onClick={() => onEdit(element)}
            className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={`Edit ${element.name}`}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>

      {open && children.length > 0 && (
        <ul>
          {children.map((child) => (
            <Node
              key={child.id}
              element={child}
              childrenOf={childrenOf}
              depth={depth + 1}
              onSelect={onSelect}
              onEdit={onEdit}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function NetworkPage() {
  const [selected, setSelected] = useState<Element | null>(null);
  const { can } = useSession();
  /** null when closed; otherwise adding a new element, or editing one. */
  const [editor, setEditor] = useState<
    { mode: 'new' } | { mode: 'edit'; element: EditableElement } | null
  >(null);

  const network = useQuery({ queryKey: ['network'], queryFn: api.network });
  const impact = useQuery({
    queryKey: ['descendants', selected?.id],
    queryFn: () => api.descendants(selected!.id),
    enabled: Boolean(selected),
  });

  const { roots, childrenOf } = useMemo(() => {
    const elements = (network.data?.elements ?? []) as Element[];
    const map = new Map<string | null, Element[]>();
    for (const element of elements) {
      const key = element.parentId;
      map.set(key, [...(map.get(key) ?? []), element]);
    }
    return { roots: map.get(null) ?? [], childrenOf: map };
  }, [network.data]);

  // Memoised for the `?? []`: a fresh empty array on every render would make
  // the grouping below recompute on every render too, for no new data.
  const coverage = useMemo(() => network.data?.coverage ?? [], [network.data]);
  const covered = coverage.filter((area) => area.elementCount > 0).length;

  // Grouped by zone, because that is the unit a dispatcher thinks in when
  // deciding who to send — Z2 is one run, not ten separate places.
  const byZone = useMemo(() => {
    const map = new Map<string, typeof coverage>();
    for (const area of coverage) {
      map.set(area.zoneCode, [...(map.get(area.zoneCode) ?? []), area]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [coverage]);

  if (network.isPending) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-1.5 font-heading text-2xl">
            Network
            <HelpHint>
              One fault at a parent affects every child. Select an element to see exactly which
              clients are below it.
            </HelpHint>
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {network.data?.elements.length} elements
          </p>
        </div>

        {/* Admin only. The tree is the map everything else reasons from. */}
        {can('network:write') && !editor && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setEditor({ mode: 'new' })}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add element
          </button>
        )}
      </div>

      {editor && (
        <NetworkEditor
          elements={(network.data?.elements ?? []) as EditableElement[]}
          areas={(network.data?.coverage ?? []).map((area) => ({ id: area.id, name: area.name }))}
          editing={editor.mode === 'edit' ? editor.element : null}
          onClose={() => setEditor(null)}
        />
      )}

      {/*
        The sketch answers "where is the break and what is behind it", which a
        nested list cannot — the shape is the information. The tree below stays
        because it is the faster way to find one named element.
      */}
      <section className="card">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
          <h3 className="flex items-center gap-1.5 font-heading text-lg">
            Connection sketch
            <HelpHint>
              How the network is wired, not where it is on the ground — we hold no survey
              coordinates, so left to right is connection order. A dashed red leg is not carrying
              traffic; everything to the right of it is off.
            </HelpHint>
          </h3>
          <ul className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            {[
              ['#0f9d58', 'Up'],
              ['#e8a33d', 'Degraded'],
              ['#d64545', 'Down'],
              ['#9aa0a6', 'Decommissioned'],
            ].map(([colour, label]) => (
              <li key={label} className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: colour }}
                  aria-hidden="true"
                />
                {label}
              </li>
            ))}
          </ul>
        </div>
        <NetworkMap
          elements={(network.data?.elements ?? []) as MapElement[]}
          selectedId={selected?.id ?? null}
          onSelect={(element) => setSelected(element as Element)}
        />
      </section>

      <section className="card">
        <h3 className="mb-1 flex items-center gap-1.5 font-heading text-lg">
          Where we reach
          <HelpHint>
            Every area GoSpeedNet serves, and how many network elements sit in it. An area showing
            nothing is one we sell in but have not recorded any equipment for.
          </HelpHint>
        </h3>
        <p className="mb-3 text-sm text-muted-foreground">
          {covered} of {coverage.length} areas have equipment recorded.
        </p>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {byZone.map(([zoneCode, areas]) => (
            <div key={zoneCode}>
              <h4 className="mb-1.5 text-xs font-semibold label-mono tracking-[0.12em] text-muted-foreground">
                {zoneCode} · {areas[0]?.zoneName}
              </h4>
              <ul className="space-y-1 text-sm">
                {areas.map((area) => (
                  <li key={area.id} className="flex items-center justify-between gap-2">
                    <span className={area.elementCount === 0 ? 'text-muted-foreground' : ''}>
                      {area.name}
                    </span>
                    <span
                      className={`badge shrink-0 ${
                        area.elementCount > 0
                          ? 'bg-secondary-50 text-secondary-700'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {area.elementCount || '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="card lg:col-span-2">
          <h3 className="mb-2 font-heading text-lg">All elements</h3>
          <ul>
            {roots.map((root) => (
              <Node
                key={root.id}
                element={root}
                childrenOf={childrenOf}
                depth={0}
                onSelect={setSelected}
                onEdit={
                  can('network:write')
                    ? (element) => setEditor({ mode: 'edit', element })
                    : undefined
                }
              />
            ))}
          </ul>
        </div>

        <div className="card">
          <h3 className="mb-3 font-heading text-lg">Impact</h3>
          {!selected && (
            <p className="text-sm text-muted-foreground">
              Select an element to see what sits below it.
            </p>
          )}

          {selected && impact.isPending && (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          )}

          {selected && impact.data && (
            <>
              <p className="text-sm">
                <strong>{impact.data.root.name}</strong>
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                <Wifi className="h-4 w-4" aria-hidden="true" />
                {impact.data.affectedCustomers} customer connection
                {impact.data.affectedCustomers === 1 ? '' : 's'} below this
              </p>

              <ul className="mt-3 space-y-1 text-sm">
                {impact.data.descendants.map((node) => (
                  <li key={node.id} style={{ paddingLeft: `${(node.depth - 1) * 12}px` }}>
                    <span className="label-mono text-xs text-muted-foreground">{node.type}</span>{' '}
                    {node.name}
                  </li>
                ))}
              </ul>

              {impact.data.descendants.length === 0 && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Nothing below this — it is a leaf.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
